/**
 * API Context for dependency injection of server calls
 * Follows ADR-007: Loose Coupling - Inject API client via context
 */

import React, { createContext, useContext, useMemo, type ReactNode } from 'react';
import { ParlantClient } from 'parlant-client';

/**
 * API client interface for chat operations
 */
export interface ApiClient {
  /** The ParlantClient instance */
  client: ParlantClient;
  /** Base server URL */
  serverUrl: string;
  /** Authentication token */
  authToken?: string;
  /** Make an authenticated request */
  authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>;
}

/**
 * Context for API client
 */
const ApiContext = createContext<ApiClient | null>(null);

/**
 * Props for ApiProvider
 */
interface ApiProviderProps {
  /** Server URL */
  serverUrl: string;
  /** Authentication token */
  authToken?: string;
  /** Children */
  children: ReactNode;
}

/**
 * Creates authenticated fetch function
 */
function createAuthenticatedFetch(
  serverUrl: string,
  authToken?: string
): (url: string, options?: RequestInit) => Promise<Response> {
  const cleanServerUrl = serverUrl.endsWith('/') ? serverUrl.slice(0, -1) : serverUrl;

  return async (url: string, options: RequestInit = {}): Promise<Response> => {
    const headers = new Headers(options.headers);
    
    if (authToken) {
      headers.set('Authorization', `Bearer ${authToken}`);
    }
    
    if (!headers.has('Content-Type') && options.method !== 'GET') {
      headers.set('Content-Type', 'application/json');
    }

    const fullUrl = url.startsWith('http') ? url : `${cleanServerUrl}${url}`;

    return fetch(fullUrl, {
      ...options,
      headers
    });
  };
}

/**
 * Provider component for API context
 */
export function ApiProvider({ 
  serverUrl, 
  authToken, 
  children 
}: ApiProviderProps): React.JSX.Element {
  const apiClient = useMemo<ApiClient>(() => {
    const client = new ParlantClient({ environment: serverUrl });
    const authenticatedFetch = createAuthenticatedFetch(serverUrl, authToken);

    return {
      client,
      serverUrl,
      authToken,
      authenticatedFetch
    };
  }, [serverUrl, authToken]);

  return (
    <ApiContext.Provider value={apiClient}>
      {children}
    </ApiContext.Provider>
  );
}

/**
 * Hook to access the API client
 * @throws Error if used outside of ApiProvider
 */
export function useApi(): ApiClient {
  const api = useContext(ApiContext);
  
  if (!api) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  
  return api;
}

/**
 * Hook to access the API client (optional - returns null if outside provider)
 */
export function useOptionalApi(): ApiClient | null {
  return useContext(ApiContext);
}

/**
 * Higher-order component to inject API client as prop
 */
export function withApi<P extends object>(
  Component: React.ComponentType<P & { api: ApiClient }>
): React.FC<P> {
  const WithApiComponent: React.FC<P> = (props) => {
    const api = useApi();
    return <Component {...props} api={api} />;
  };
  
  WithApiComponent.displayName = `WithApi(${Component.displayName || Component.name || 'Component'})`;
  
  return WithApiComponent;
}

